// wordSearchGrid.js
// Ports escape_peace-old/js/wordpuzzle.js's grid generation and selection-path
// logic verbatim (including its exact quirks) so the Chest activity matches
// the real, currently-executing PHP behavior rather than the plan's
// paraphrase. Two confirmed corrections from reading the actual JS:
//   1. Word placement (and therefore valid drag selection) supports
//      horizontal, vertical, AND down-right diagonal — not just H/V. The
//      Phase 3 instructions said "don't add diagonal unless PHP supports
//      it"; it does (`Math.floor(Math.random() * 3)` picks direction
//      0/1/2), so diagonal is included here.
//   2. The non-scoring distractor word is spelled "IGNORANT" in the JS that
//      actually generates the grid, not "gnorant" as the plan/PHP's unused
//      `$words` variable has it (that PHP variable is declared but never
//      output — wordpuzzle.js is what really runs). Doesn't affect scoring
//      either way (still a non-positive, non-scoring word).
export const GRID_SIZE = 10;
export const SOURCE_WORDS = ['CONFIDENT', 'RESILIENT', 'KIND', 'WORTHY', 'IGNORANT', 'LAZY', 'DOUBTFUL'];
export const POSITIVE_WORDS = ['CONFIDENT', 'RESILIENT', 'KIND', 'WORTHY'];

function createEmptyGrid(size) {
  return Array.from({ length: size }, () => Array(size).fill(''));
}

function canPlaceWord(grid, word, row, col, direction) {
  const size = grid.length;
  if (direction === 0) {
    // horizontal
    if (col + word.length > size) return false;
    for (let i = 0; i < word.length; i++) {
      if (grid[row][col + i] !== '') return false;
    }
  } else if (direction === 1) {
    // vertical
    if (row + word.length > size) return false;
    for (let i = 0; i < word.length; i++) {
      if (grid[row + i][col] !== '') return false;
    }
  } else {
    // diagonal (down-right)
    if (row + word.length > size || col + word.length > size) return false;
    for (let i = 0; i < word.length; i++) {
      if (grid[row + i][col + i] !== '') return false;
    }
  }
  return true;
}

function placeWord(grid, word, row, col, direction) {
  for (let i = 0; i < word.length; i++) {
    if (direction === 0) grid[row][col + i] = word[i];
    else if (direction === 1) grid[row + i][col] = word[i];
    else grid[row + i][col + i] = word[i];
  }
}

function placeWordsInGrid(grid, words) {
  const size = grid.length;
  words.forEach((word) => {
    let placed = false;
    let attempts = 0;
    while (!placed && attempts < 100) {
      const direction = Math.floor(Math.random() * 3);
      const row = Math.floor(Math.random() * size);
      const col = Math.floor(Math.random() * size);
      if (canPlaceWord(grid, word, row, col, direction)) {
        placeWord(grid, word, row, col, direction);
        placed = true;
      }
      attempts += 1;
    }
    // If a word can't be placed after 100 attempts it's silently skipped,
    // matching room.php (console.error, no throw).
  });
}

function fillGridWithRandomLetters(grid) {
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      if (grid[row][col] === '') {
        grid[row][col] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
      }
    }
  }
}

export function generateWordSearchGrid() {
  const grid = createEmptyGrid(GRID_SIZE);
  placeWordsInGrid(grid, SOURCE_WORDS);
  fillGridWithRandomLetters(grid);
  return grid;
}

// A drag is only valid rightward, downward, or down-right diagonal — the
// same restriction as room.php's highlightPath (`deltaX >= 0 && deltaY >= 0`).
export function isValidSelectionPath(startRow, startCol, endRow, endCol) {
  const deltaX = endCol - startCol;
  const deltaY = endRow - startRow;
  const absX = Math.abs(deltaX);
  const absY = Math.abs(deltaY);
  return (absX === absY || deltaX === 0 || deltaY === 0) && deltaX >= 0 && deltaY >= 0;
}

export function cellsAlongPath(startRow, startCol, endRow, endCol) {
  if (!isValidSelectionPath(startRow, startCol, endRow, endCol)) return [];
  const deltaX = endCol - startCol;
  const deltaY = endRow - startRow;
  const absX = Math.abs(deltaX);
  const absY = Math.abs(deltaY);
  const stepX = deltaX === 0 ? 0 : deltaX / absX;
  const stepY = deltaY === 0 ? 0 : deltaY / absY;
  const steps = Math.max(absX, absY);
  const cells = [];
  for (let i = 0; i <= steps; i++) {
    cells.push({ row: startRow + i * stepY, col: startCol + i * stepX });
  }
  return cells;
}
